import React from 'react';

import { cn } from '@/lib/utils';
import { useUserStatusStore } from '@/store/useUserStatusStore';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// ==================== Types ====================
export interface UserStatusBadgeProps {
  userId: string;
  showLastSeen?: boolean;
  size?: 'sm' | 'md' | 'lg';
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
  className?: string;
  showTooltip?: boolean;
}

// ==================== Component ====================
export const UserStatusBadge = React.memo(function UserStatusBadge({
  userId,
  showLastSeen = true,
  size = 'md',
  position = 'bottom-right',
  className,
  showTooltip = true,
}: UserStatusBadgeProps) {
  const getStatus = useUserStatusStore((state) => state.getStatus);
  const getLastSeenFormatted = useUserStatusStore(
    (state) => state.getLastSeenFormatted,
  );

  const status = getStatus(userId);
  const lastSeen = getLastSeenFormatted(userId);

  if (!status) {
    return null;
  }

  // Size classes for the badge
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  // Position classes for absolute positioning
  const positionClasses = {
    'top-right': 'top-0 right-0',
    'bottom-right': 'bottom-0 right-0',
    'top-left': 'top-0 left-0',
    'bottom-left': 'bottom-0 left-0',
  };

  // Badge element
  const badgeElement = (
    <span
      className={cn(
        'absolute rounded-full border-2 border-white dark:border-gray-800',
        status.isOnline ? 'bg-green-500' : 'bg-gray-400',
        sizeClasses[size],
        positionClasses[position],
        className,
      )}
      aria-label={status.isOnline ? 'Online' : 'Offline'}
    />
  );

  // Show tooltip if enabled
  if (showTooltip && showLastSeen && lastSeen) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="relative inline-block">{badgeElement}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            <p>{status.empName || 'User'}</p>
            <p className="text-muted-foreground">{lastSeen}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badgeElement;
});

// ==================== Standalone Status Indicator ====================
export interface UserStatusIndicatorProps {
  userId: string;
  showName?: boolean;
  showLastSeen?: boolean;
  className?: string;
}

export const UserStatusIndicator = React.memo(function UserStatusIndicator({
  userId,
  showName = true,
  showLastSeen = true,
  className,
}: UserStatusIndicatorProps) {
  const getStatus = useUserStatusStore((state) => state.getStatus);
  const getLastSeenFormatted = useUserStatusStore(
    (state) => state.getLastSeenFormatted,
  );

  const status = getStatus(userId);
  const lastSeen = getLastSeenFormatted(userId);

  if (!status) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span
        className={cn(
          'w-2 h-2 rounded-full',
          status.isOnline ? 'bg-green-500' : 'bg-gray-400',
        )}
        aria-label={status.isOnline ? 'Online' : 'Offline'}
      />
      <div className="flex flex-col">
        {showName && status.empName && (
          <span className="text-sm font-medium">{status.empName}</span>
        )}
        {showLastSeen && lastSeen && (
          <span className="text-xs text-muted-foreground">{lastSeen}</span>
        )}
      </div>
    </div>
  );
});
